
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ModuleConnectionInfo struct
 */
class ModuleConnectionInfo {
  /**
   * Initialize struct
   * @param {!UUID=} moduleId
   * @param {!Date=} connectionTime
   * @param {!number=} localPort
   * @param {!number=} remotePort
   * @param {!string=} remoteAddress
   * @constructor
   */
  constructor (moduleId = new UUID(), connectionTime = new Date(0), localPort = 0, remotePort = 0, remoteAddress = '') {
    this.ModuleId = moduleId
    this.ConnectionTime = connectionTime
    this.LocalPort = localPort
    this.RemotePort = remotePort
    this.RemoteAddress = remoteAddress
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ModuleConnectionInfo}
   * @param {!ModuleConnectionInfo} other Other struct
   * @returns {!ModuleConnectionInfo} This struct
   */
  copy (other) {
    if (other.ModuleId != null) {
      this.ModuleId = new UUID(other.ModuleId)
    } else {
      this.ModuleId = null
    }
    if (other.ConnectionTime != null) {
      if (other.ConnectionTime instanceof Date) {
        this.ConnectionTime = new Date(other.ConnectionTime.getTime())
      } else {
        this.ConnectionTime = new Date(Math.round(other.ConnectionTime / 1000000))
      }
    } else {
      this.ConnectionTime = null
    }
    if (other.LocalPort != null) {
      this.LocalPort = other.LocalPort
    } else {
      this.LocalPort = null
    }
    if (other.RemotePort != null) {
      this.RemotePort = other.RemotePort
    } else {
      this.RemotePort = null
    }
    if (other.RemoteAddress != null) {
      this.RemoteAddress = other.RemoteAddress
    } else {
      this.RemoteAddress = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ModuleConnectionInfo}
   * @returns {!ModuleConnectionInfo} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ModuleConnectionInfoModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ModuleConnectionInfoModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ModuleConnectionInfo}
   * @param {!ModuleConnectionInfo} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ModuleConnectionInfo)) {
      throw new TypeError('Instance of ModuleConnectionInfo is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ModuleConnectionInfo}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      ModuleId: ((this.ModuleId != null) ? this.ModuleId.toString() : null),
      ConnectionTime: ((this.ConnectionTime != null) ? (this.ConnectionTime.getTime() * 1000000) : null),
      LocalPort: ((this.LocalPort != null) ? this.LocalPort : null),
      RemotePort: ((this.RemotePort != null) ? this.RemotePort : null),
      RemoteAddress: ((this.RemoteAddress != null) ? this.RemoteAddress : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ModuleConnectionInfo.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ModuleConnectionInfo} other Object value
   * @returns {!ModuleConnectionInfo} Created struct
   */
  static fromObject (other) {
    return new ModuleConnectionInfo().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ModuleConnectionInfo}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ModuleConnectionInfo.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ModuleConnectionInfo}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 78
  }
}

export { ModuleConnectionInfo };

/**
 * Fast Binary Encoding ModuleConnectionInfo field model
 */
class FieldModelModuleConnectionInfo extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.ModuleId = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.ConnectionTime = new fbe.FieldModelTimestamp(buffer, this.ModuleId.fbeOffset + this.ModuleId.fbeSize)
    this.LocalPort = new fbe.FieldModelUInt16(buffer, this.ConnectionTime.fbeOffset + this.ConnectionTime.fbeSize)
    this.RemotePort = new fbe.FieldModelUInt16(buffer, this.LocalPort.fbeOffset + this.LocalPort.fbeSize)
    this.RemoteAddress = new fbe.FieldModelString(buffer, this.RemotePort.fbeOffset + this.RemotePort.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelModuleConnectionInfo}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelModuleConnectionInfo}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.ModuleId.fbeSize + this.ConnectionTime.fbeSize + this.LocalPort.fbeSize + this.RemotePort.fbeSize + this.RemoteAddress.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelModuleConnectionInfo}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.ModuleId.fbeExtra + this.ConnectionTime.fbeExtra + this.LocalPort.fbeExtra + this.RemotePort.fbeExtra + this.RemoteAddress.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelModuleConnectionInfo}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelModuleConnectionInfo.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelModuleConnectionInfo}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 78
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelModuleConnectionInfo}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelModuleConnectionInfo}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ModuleId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ModuleId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ModuleId.fbeSize

    if ((fbeCurrentSize + this.ConnectionTime.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ConnectionTime.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ConnectionTime.fbeSize

    if ((fbeCurrentSize + this.LocalPort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LocalPort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LocalPort.fbeSize

    if ((fbeCurrentSize + this.RemotePort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RemotePort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RemotePort.fbeSize

    if ((fbeCurrentSize + this.RemoteAddress.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RemoteAddress.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RemoteAddress.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelModuleConnectionInfo}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelModuleConnectionInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelModuleConnectionInfo}
   * @param {!ModuleConnectionInfo} fbeValue Default value, defaults is new ModuleConnectionInfo()
   * @returns {!ModuleConnectionInfo} ModuleConnectionInfo value
   */
  get (fbeValue = new ModuleConnectionInfo()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelModuleConnectionInfo}
   * @param {!ModuleConnectionInfo} fbeValue ModuleConnectionInfo value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ModuleId.fbeSize) <= fbeStructSize) {
      fbeValue.ModuleId = this.ModuleId.get()
    } else {
      fbeValue.ModuleId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ModuleId.fbeSize

    if ((fbeCurrentSize + this.ConnectionTime.fbeSize) <= fbeStructSize) {
      fbeValue.ConnectionTime = this.ConnectionTime.get()
    } else {
      fbeValue.ConnectionTime = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ConnectionTime.fbeSize

    if ((fbeCurrentSize + this.LocalPort.fbeSize) <= fbeStructSize) {
      fbeValue.LocalPort = this.LocalPort.get()
    } else {
      fbeValue.LocalPort = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LocalPort.fbeSize

    if ((fbeCurrentSize + this.RemotePort.fbeSize) <= fbeStructSize) {
      fbeValue.RemotePort = this.RemotePort.get()
    } else {
      fbeValue.RemotePort = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RemotePort.fbeSize

    if ((fbeCurrentSize + this.RemoteAddress.fbeSize) <= fbeStructSize) {
      fbeValue.RemoteAddress = this.RemoteAddress.get()
    } else {
      fbeValue.RemoteAddress = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RemoteAddress.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelModuleConnectionInfo}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelModuleConnectionInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelModuleConnectionInfo}
   * @param {!ModuleConnectionInfo} fbeValue ModuleConnectionInfo value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelModuleConnectionInfo}
   * @param {ModuleConnectionInfo} fbeValue ModuleConnectionInfo value
   */
  setFields (fbeValue) {
    this.ModuleId.set(fbeValue.ModuleId)
    this.ConnectionTime.set(fbeValue.ConnectionTime)
    this.LocalPort.set(fbeValue.LocalPort)
    this.RemotePort.set(fbeValue.RemotePort)
    this.RemoteAddress.set(fbeValue.RemoteAddress)
  }
}

export { FieldModelModuleConnectionInfo };

/**
 * Fast Binary Encoding ModuleConnectionInfo model
 */
class ModuleConnectionInfoModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelModuleConnectionInfo(this.buffer, 4)
  }

  /**
   * Get the ModuleConnectionInfo model
   * @this {!ModuleConnectionInfoModel}
   * @returns {!FieldModelModuleConnectionInfo} model ModuleConnectionInfo model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ModuleConnectionInfoModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ModuleConnectionInfoModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ModuleConnectionInfoModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ModuleConnectionInfoModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelModuleConnectionInfo.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ModuleConnectionInfoModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ModuleConnectionInfoModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ModuleConnectionInfoModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ModuleConnectionInfoModel}
   * @param {!ModuleConnectionInfo} value ModuleConnectionInfo value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ModuleConnectionInfoModel}
   * @param {!ModuleConnectionInfo} value ModuleConnectionInfo value, defaults is new ModuleConnectionInfo()
   * @return {!object} Deserialized ModuleConnectionInfo value and its size
   */
  deserialize (value = new ModuleConnectionInfo()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ModuleConnectionInfo(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ModuleConnectionInfo(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ModuleConnectionInfoModel}
   * @param {!number} prev Previous ModuleConnectionInfo model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ModuleConnectionInfoModel };
